import React, { forwardRef, useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import PropTypes from "prop-types";
import { TextField } from "@mui/material";
import { Controller } from "react-hook-form";
import IndividualMembershipController from "../../../src/pages/Admin/Controller/individual_membership_controller.ts";
import CorporateMembershipController from "../../../src/pages/Admin/Controller/corporate_membership_controller.ts";
import EventController from "../../../src/pages/Admin/Controller/event_controller.ts";

function getStyles(name, personName, theme) {
  return {
    fontWeight: personName.includes(name)
      ? theme.typography.fontWeightMedium
      : theme.typography.fontWeightRegular,
  };
}

const MultipleSelectChip = forwardRef(
  (
    { type, onErrorCheck, control, name, getValues, clear, setValue, ...props },
    ref
  ) => {
    const theme = useTheme();
    const [personName, setPersonName] = React.useState([]);
    const [member_typeIDs, setMemberTypeIDs] = useState([]);
    const [committee_typeIDs, setCommitteeTypeIDs] = useState([]);
    const [committee_membersRow, setCommitteeMembers] = useState([]);
    const [secretariat_membersRow, setSecretariatMembers] = useState([]);
    const [open, setOpen] = useState(false);

    const handleChange = (event, onChange) => {
      const {
        target: { value },
      } = event;
      // Handle value change and update local state

      const selectedValues =
        typeof value === "string" ? value.split(",") : value;

      setPersonName(selectedValues);
      // Update the value in the form control
      onChange(selectedValues);
    };

    useEffect(() => {
      IndividualMembershipController.getMemberType().then((response) => {
        const member_typeIDs = response.map((item, index) => ({
          value: item.member_typeID,
          label: item.label,
        }));
        setMemberTypeIDs(member_typeIDs);
      });

      CorporateMembershipController.getCommitteeType().then((response) => {
        const committee_typeIDs = response.map((item, index) => ({
          value: item.committee_typeID,
          label: item.label,
        }));
        setCommitteeTypeIDs(committee_typeIDs);
      });

      EventController.getCommitteeMembers().then((response) => {
        const committee_membersRow = response.map((item, index) => ({
          value: item.userID,
          label: item.name,
        }));
        setCommitteeMembers(committee_membersRow);
      });

      EventController.getSecretariatMembers().then((response) => {
        const secretariat_membersRow = response.map((item, index) => ({
          value: item.userID,
          label: item.name,
        }));
        setSecretariatMembers(secretariat_membersRow);
      });
    }, []);

    const accountType = [
      { value: 0, label: "Appointed Account" },
      { value: 1, label: "Alternate Account" },
    ];

    const handleOpen = () => {
      setOpen(true);
    };

    const handleClose = () => {
      setOpen(false);
    };
    // Effect to handle `clear` prop changes
    useEffect(() => {
      if (clear) {
        setPersonName([]); // Clear local state
        setValue(name, []); // Clear form control state in react-hook-form
      }
    }, [clear, name, setValue]);

    return (
      <Controller
        name={name}
        control={control}
        render={({
          field: { value = [], onChange },
          fieldState: { error },
        }) => {
          return (
            <TextField
              select
              value={value || personName}
              onChange={(event) => handleChange(event, onChange)} // Pass onChange to handleChange
              slotProps={{
                select: {
                  multiple: true,
                  renderValue: (selected) => {
                    return (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((valueSelected) => {
                          // Find the item in member_typeIDs that matches the selected value
                          const item =
                            name === "resourcesMembershipAccess"
                              ? member_typeIDs.find(
                                  (item) => item.value === valueSelected
                                )
                              : name === "resourcesAccessAccountType"
                              ? accountType.find(
                                  (item) => item.value === valueSelected
                                )
                              : name === "resourcesAccessCommitteeType"
                              ? committee_typeIDs.find(
                                  (item) => item.value === valueSelected
                                )
                              : name === "CommitteeMembers"
                              ? committee_membersRow.find(
                                  (item) => item.value === valueSelected
                                )
                              : name === "SecretariatMembers"
                              ? secretariat_membersRow.find(
                                  (item) => item.value === valueSelected
                                )
                              : null;

                          // Render a Chip with the item's label if it exists
                          return item ? (
                            <Chip key={valueSelected} label={item.label} />
                          ) : null;
                        })}
                      </Box>
                    );
                  },
                  onOpen: handleOpen,
                  onClose: handleClose,
                  open: open,
                },
              }}
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  fontSize: "16px",
                  borderRadius: "25px !important",
                  color: "black",
                  backgroundColor: "white !important",
                  "& fieldset": {
                    borderColor: "#ccc",
                  },
                  "&:hover fieldset": {
                    borderColor: "#999",
                  },
                  // "&.Mui-focused fieldset": {
                  //   borderColor: "transparent",
                  //   outline: "none",
                  // },
                },
              }}
              error={Boolean(onErrorCheck || error)}
              helperText={onErrorCheck || error?.message || ""}
            >
              {name === "resourcesMembershipAccess" &&
                member_typeIDs.map((name) => (
                  <MenuItem
                    key={name.value}
                    value={name.value}
                    style={getStyles(name, personName, theme)}
                  >
                    {name.label}
                  </MenuItem>
                ))}

              {name === "resourcesAccessAccountType" &&
                accountType.map((name) => (
                  <MenuItem key={name.value} value={name.value}>
                    {name.label}
                  </MenuItem>
                ))}

              {name === "resourcesAccessCommitteeType" &&
                committee_typeIDs.map((name) => (
                  <MenuItem key={name.value} value={name.value}>
                    {name.label}
                  </MenuItem>
                ))}

              {name === "CommitteeMembers" &&
                committee_membersRow.map((name) => (
                  <MenuItem key={name.value} value={name.value}>
                    {name.label}
                  </MenuItem>
                ))}

              {name === "SecretariatMembers" &&
                secretariat_membersRow.map((name) => (
                  <MenuItem key={name.value} value={name.value}>
                    {name.label}
                  </MenuItem>
                ))}
            </TextField>
          );
        }}
      />
    );
  }
);

MultipleSelectChip.propTypes = {
  type: PropTypes.string,
  onErrorCheck: PropTypes.string,
};

export default MultipleSelectChip;
